import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Plus, Edit2, Trash2, Search, X, Loader2, RefreshCw, 
  CheckCircle2, XCircle, AlertCircle, Building, MapPin, Phone 
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../../common/apiClient';
import SummaryApi from '../../../../common/SummaryApi';
import SelectDropdown from '../../../../customStyles/SelectDropdown';

const BranchList = () => {
  const navigate = useNavigate();
  const { portalName } = useParams();

  const [branches, setBranches] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [divisionFilter, setDivisionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Delete Modal State
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, item: null, loading: false });

  // Fetch Divisions for filter dropdown
  const fetchDivisions = useCallback(async () => {
    try {
      const response = await api.get(SummaryApi.getAllDivisions.url);
      if (response.data?.success) {
        setDivisions(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching divisions:', error);
    }
  }, []);

  // Fetch All Branches
  const fetchBranches = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(SummaryApi.getAllBranches.url);
      if (response.data?.success) {
        setBranches(response.data.data || []);
      } else {
        toast.error(response.data?.message || 'Failed to fetch branches');
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch branches');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDivisions();
    fetchBranches();
  }, [fetchDivisions, fetchBranches]);

  // Delete Confirmation Handler
  const handleDeleteConfirm = async () => {
    if (!deleteModal.item) return;

    setDeleteModal((prev) => ({ ...prev, loading: true }));
    try {
      const url = typeof SummaryApi.deleteBranch.url === 'function'
        ? SummaryApi.deleteBranch.url(deleteModal.item._id)
        : SummaryApi.deleteBranch.url;

      const response = await api.delete(url);
      if (response.data?.success) {
        toast.success('Branch deleted successfully');
        setDeleteModal({ isOpen: false, item: null, loading: false });
        fetchBranches();
      } else {
        toast.error(response.data?.message || 'Delete failed');
      }
    } catch (error) {
      console.error('Error deleting branch:', error);
      toast.error(error.response?.data?.message || 'Failed to delete branch');
    } finally {
      setDeleteModal((prev) => ({ ...prev, loading: false }));
    }
  };

  // Client-side Filtering
  const filteredBranches = useMemo(() => {
    return branches.filter((item) => {
      const matchesSearch =
        item.branchName?.toLowerCase().includes(searchTerm.toLowerCase().trim()) ||
        item.branchCode?.toLowerCase().includes(searchTerm.toLowerCase().trim()) ||
        item.city?.toLowerCase().includes(searchTerm.toLowerCase().trim()) ||
        item.state?.toLowerCase().includes(searchTerm.toLowerCase().trim());

      const matchesDivision =
        divisionFilter === 'all' 
          ? true 
          : item.divisionIds?.some(div => (div._id || div) === divisionFilter);

      const matchesStatus =
        statusFilter === 'all' ? true :
        statusFilter === 'active' ? item.isActive === true :
        item.isActive === false;

      return matchesSearch && matchesDivision && matchesStatus;
    });
  }, [branches, searchTerm, divisionFilter, statusFilter]);

  return (
    <div className="p-2 space-y-3">
      {/* Top Header Card */}
      <div className="p-4 bg-white rounded-xl shadow-xs border border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-red-50 text-red-600 rounded-lg shrink-0">
              <Building className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">Branch Management</h1>
              <p className="text-xs md:text-sm text-gray-500">
                Manage organization branches, locations, head offices, and regional offices
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate(`/${portalName}/home/settings/branches/create`)}
            className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium shadow-xs transition-colors shrink-0"
          >
            <Plus className="w-4 h-4" />
            <span>Add New Branch</span>
          </button>
        </div>

        {/* Filter & Search Bar */}
        <div className="mt-5 flex flex-col md:flex-row gap-3 items-center justify-between pt-4 border-t border-gray-100">
          {/* Search Box */}
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search branch name, code, city..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-8 py-2 text-xs md:text-sm bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white transition-all"
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')} 
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Dropdown Filters */}
          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
            {/* Division Filter */}
            <div className="w-full sm:w-56">
              <SelectDropdown
                options={[
                  { _id: 'all', name: 'All Divisions' },
                  ...divisions
                ]}
                value={divisionFilter}
                onChange={(val) => setDivisionFilter(val)}
                placeholder="Filter by Division"
                valueKey="_id"
                labelKey="name"
                searchable={true}
              />
            </div>

            {/* Status Filter */}
            <div className="w-full sm:w-36">
              <SelectDropdown
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'active', label: 'Active Only' },
                  { value: 'inactive', label: 'Inactive Only' },
                ]}
                value={statusFilter}
                onChange={(val) => setStatusFilter(val)}
                placeholder="Select Status"
                valueKey="value"
                labelKey="label"
                searchable={false}
              />
            </div>

            <button
              onClick={() => { fetchDivisions(); fetchBranches(); }}
              disabled={loading}
              className="p-2 border border-gray-300 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Branches Table */}
      <div className="bg-white rounded-xl shadow-xs border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center p-12">
            <Loader2 className="w-8 h-8 text-red-600 animate-spin mb-3" />
            <p className="text-sm text-gray-500">Loading branches...</p>
          </div>
        ) : filteredBranches.length === 0 ? (
          <div className="text-center p-12">
            <Building className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-gray-700">No branches found</h3>
            <p className="text-xs text-gray-400 mt-1">
              {searchTerm || divisionFilter !== 'all' || statusFilter !== 'all'
                ? 'Try adjusting your search query or filters'
                : 'Click "Add New Branch" to register your first branch'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-left text-xs md:text-sm">
              <thead className="bg-gray-50 text-gray-600 font-semibold uppercase tracking-wider text-[11px]">
                <tr>
                  <th className="py-3.5 px-4 w-12 text-center">S.No</th>
                  <th className="py-3.5 px-4">Branch Name</th>
                  <th className="py-3.5 px-4">Branch Code</th>
                  <th className="py-3.5 px-4">Divisions</th>
                  <th className="py-3.5 px-4">Contact / Location</th>
                  <th className="py-3.5 px-4 text-center">Status</th>
                  <th className="py-3.5 px-4 text-center w-28">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredBranches.map((item, idx) => (
                  <tr key={item._id} className="hover:bg-gray-50/70 transition-colors">
                    <td className="py-3.5 px-4 text-center text-gray-500">{idx + 1}</td>

                    {/* Branch Name & Head Office Badge */}
                    <td className="py-3.5 px-4">
                      <div className="flex items-center gap-2">
                        <span
                          onClick={() => navigate(`/${portalName}/home/settings/branches/view/${item._id}`)}
                          className="font-bold text-red-600 cursor-pointer transition-colors"
                          title="View Branch Details"
                        >
                          {item.branchName}
                        </span>
                        
                        {item.isMainBranch && (
                          <span className="px-2 py-0.5 text-[10px] font-bold bg-amber-100 text-amber-800 rounded">
                            Head Office
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Branch Code */}
                    <td className="py-3.5 px-4">
                      <span className="inline-block px-2 py-0.5 font-mono text-xs font-semibold bg-gray-100 text-gray-800 rounded">
                        {item.branchCode}
                      </span>
                    </td>

                    {/* Divisions */}
                    <td className="py-3.5 px-4">
                      <div className="flex flex-wrap gap-1">
                        {item.divisionIds?.length > 0 ? (
                          item.divisionIds.map((div, i) => (
                            <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded text-xs font-medium">
                              {div.name || 'Division'}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-400 text-xs">All Divisions</span>
                        )}
                      </div>
                    </td>

                    {/* Contact / Location */}
                    <td className="py-3.5 px-4">
                      <div className="space-y-0.5 text-xs text-gray-600">
                        {item.city || item.district ? (
                          <div className="flex items-center gap-1 font-medium text-gray-800">
                            <MapPin className="w-3.5 h-3.5 text-red-500 shrink-0" />
                            <span>{item.city || item.district}, {item.state}</span>
                          </div>
                        ) : null}
                        {item.contactPhone && (
                          <div className="flex items-center gap-1">
                            <Phone className="w-3 h-3 text-gray-400 shrink-0" />
                            <span>{item.contactPhone}</span>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Status */}
                    <td className="py-3.5 px-4 text-center">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        item.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {item.isActive ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                        {item.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="py-3.5 px-4">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          onClick={() => navigate(`/${portalName}/home/settings/branches/edit/${item._id}`)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit Branch"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteModal({ isOpen: true, item, loading: false })}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Branch"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs animate-fade-in">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5 text-center">
            <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-3">
              <AlertCircle className="w-6 h-6" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-1">Delete Branch?</h3>
            <p className="text-xs text-gray-500 mb-4">
              Are you sure you want to delete branch <strong className="text-gray-800">{deleteModal.item?.branchName}</strong>? 
              This action cannot be undone.
            </p>
            <div className="flex justify-center gap-2">
              <button
                type="button"
                onClick={() => setDeleteModal({ isOpen: false, item: null, loading: false })}
                disabled={deleteModal.loading}
                className="px-4 py-2 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={deleteModal.loading}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg shadow-xs disabled:opacity-60"
              >
                {deleteModal.loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                <span>Delete Branch</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BranchList;